/// <amd-module name="Feed/Legacy/Protocols/Ivy/IvyConnectToFeedPanel" />
import * as React from "react";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageService } from "VSS/Platform/Page";
import * as Url from "VSS/Platform/Util/Url";

import { CiConstants } from "Feed/Common/Constants/Constants";
import * as PackageResources from "Feed/Common/Resources";
import { ExternalLink } from "Feed/Legacy/Components/ExternalLink";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { IConnectToFeedProps } from "Feed/Legacy/Protocols/Common/IConnectToFeedProps";
import { CopyableTextField, ICopyableTextFieldTelemetry } from "Feed/Legacy/Protocols/Components/CopyableTextField";
import { IvyCiConstants, IvyExternalLinks } from "Feed/Legacy/Protocols/Ivy/Constants/IvyConstants";
import { IIvyRestApiUrlGeneratorService } from "Feed/Legacy/Protocols/Ivy/IvyRestApiUrlGeneratorService";
import { HubAction } from "Feed/Legacy/Types/IHubState";

export interface IIvyConnectToFeedPanelProps extends IConnectToFeedProps {}

export interface IIvyConnectToFeedPanelState {
    endpointUrl: string;
    isAuthTokenLoading?: boolean;
}

export class IvyConnectToFeedPanel extends React.Component<IIvyConnectToFeedPanelProps, IIvyConnectToFeedPanelState> {
    private _generatedCredentials: CopyableTextField;

    constructor(props: IIvyConnectToFeedPanelProps) {
        super(props);

        this.state = {
            endpointUrl: this.props.pageContext
                .getService<IIvyRestApiUrlGeneratorService>("IIvyRestApiUrlGeneratorService")
                .getEndpointUrl(this.props.feed.name)
        } as IIvyConnectToFeedPanelState;

        CustomerIntelligenceHelper.publishEvent(this.props.pageContext, CiConstants.ConnectToFeed, {
            Action: CiConstants.ConnectToFeedTabChanged,
            Protocol: IvyCiConstants.ProtocolName,
            Context: HubAction[this.props.hubAction]
        });
    }

    public render(): JSX.Element {
        return (
            <div className="ivy-connect-to-feed-panel connect-to-feed-panel">
                <div className="connect-to-feed-section">
                    <div className="connect-to-feed-section-title">
                        {PackageResources.IvyConnectControl_HeadingText}
                    </div>
                    <div className="connect-to-feed-sub-heading">
                        {PackageResources.IvyConnectControl_RepositoryUrl}
                    </div>
                    <CopyableTextField
                        pageContext={this.props.pageContext}
                        text={this.state.endpointUrl}
                        buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Ivy_URL_Copy}
                        textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Ivy_URL}
                        telemetryProperties={
                            {
                                commandName: CiConstants.CopyCommandAddSource,
                                feedName: this.props.feed.name,
                                protocol: IvyCiConstants.ProtocolName
                            } as ICopyableTextFieldTelemetry
                        }
                    />
                    <div className="connect-to-feed-sub-heading">
                        <p>{PackageResources.IvyConnectControl_Credentials_SubHeading}</p>
                        <p>{PackageResources.IvyConnectControl_Credentials_note1}</p>
                        <p>{PackageResources.IvyConnectControl_Credentials_note2}</p>
                    </div>
                    <ExternalLink
                        pageContext={this.props.pageContext}
                        className="connect-to-feed-link"
                        href={IvyExternalLinks.CredentialsReference}
                        ciContext={IvyCiConstants.IvyConnectToFeedPanel}
                    >
                        {PackageResources.IvyConnectControl_ExternalLinkForCredentials}
                    </ExternalLink>
                    <div className="connect-to-feed-sub-heading">
                        <p>{PackageResources.IvyConnectControl_Settings_SubHeading}</p>
                        <p>{PackageResources.IvyConnectControl_Settings_note1}</p>
                        <p>{PackageResources.IvyConnectControl_Settings_note2}</p>
                    </div>
                    <CopyableTextField
                        pageContext={this.props.pageContext}
                        text={this._createIvySettingsTag()}
                        buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Ivy_Settings_Copy}
                        textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Ivy_Settings}
                        telemetryProperties={
                            {
                                commandName: CiConstants.CopyCommandAddSource,
                                feedName: this.props.feed.name,
                                protocol: IvyCiConstants.ProtocolName
                            } as ICopyableTextFieldTelemetry
                        }
                    />
                    <ExternalLink
                        pageContext={this.props.pageContext}
                        className="connect-to-feed-link"
                        href={IvyExternalLinks.UrlResolverReference}
                        ciContext={IvyCiConstants.IvyConnectToFeedPanel}
                    >
                        {PackageResources.IvyConnectControl_ExternalLinkForUrlResolver}
                    </ExternalLink>
                    <ExternalLink
                        pageContext={this.props.pageContext}
                        className="connect-to-feed-link"
                        href={IvyExternalLinks.SettingsReference}
                        ciContext={IvyCiConstants.IvyConnectToFeedPanel}
                    >
                        {PackageResources.IvyConnectControl_ExternalLinkForSettings}
                    </ExternalLink>
                </div>
            </div>
        );
    }

    private _createIvySettingsTag() {
        const trimStart = (str, suffix) => str.slice(suffix.length, str.length);
        const uri = new Url.Uri(this.state.endpointUrl);
        const pageData = this.props.pageContext.getService<IVssPageService>("IVssPageService").getData();
        const isHosted = pageData.isHosted;
        const realm = Utils_String.format(
            "{0}://{1}{2}{3}",
            uri.scheme,
            uri.host,
            uri.port > 0 ? ":" + uri.port : "",
            isHosted ? "" : "/tfs"
        );
        const path = isHosted ? uri.path : trimStart(uri.path, "tfs/");
        const userName = pageData.user.displayName;
        const resolverName = Utils_String.format(
            "VSTS_{0}_ivy_resolver",
            this.props.feed.name.replace(/ /g, "_")
        ).toLowerCase();
        return Utils_String.format(
            "<ivysettings>\n" +
                ' <property name="repo.host" value="{0}" />\n' +
                ' <property name="repo.realm" value="{1}" />\n' +
                ' <property name="repo.path" value="{2}" />\n' +
                ' <property name="repo.user" value="{3}" />\n' +
                ' <property name="repo.pass" value="{4}" />\n' +
                ' <property name="repo.pattern" value="[organisation]/[module]/[revision]" />\n' +
                ' <credentials host="${{repo.host}}" realm="${{repo.realm}}" username="${{repo.user}}" passwd="${{repo.pass}}" />\n' +
                ' <settings defaultResolver="{5}" />\n' +
                " <resolvers>\n" +
                '  <url name="{5}">\n' +
                '   <ivy pattern="${{repo.realm}}/${{repo.path}}/${{repo.pattern}}/ivy.xml?correlationId=${{build.id}}" />\n' +
                '   <artifact pattern="${{repo.realm}}/${{repo.path}}/${{repo.pattern}}(/[branch])/[conf]/[artifact]-[revision].[ext]?correlationId=${{build.id}}" />\n' +
                "  </url>\n" +
                " </resolvers>\n" +
                "</ivysettings>",
            uri.host,
            realm,
            path,
            userName,
            "[INSERT PAT HERE]",
            resolverName
        );
    }
}
